<template>
  <div
    id="performance"
    class="px-4"
  >
    <v-container
      v-if="eventDetails.loading || player.loading"
      class="d-flex justify-center align-center my-16"
    >
      <v-progress-circular
        indeterminate
        size="100"
      />
    </v-container>
    <div v-else>
      <v-row align="center">
        <v-col cols="auto">
          <v-btn
            icon
            @click="goBack"
          >
            <v-icon small>
              fa-light fa-chevron-left
            </v-icon>
          </v-btn>
        </v-col>
        <v-col>
          <router-link
            :to="{ name: RouteNames.PLAYER_DETAILS, params: { id: player.data.id } }"
            class="text-decoration-none primary--text font-weight-medium text-h6 "
          >
            {{ player.data.first_name }} {{ player.data.last_name }}
          </router-link>
          <span class="mx-2 grey--text">/</span>
          <span class="text-h6 font-weight-medium text-none">
            {{ eventDetails.data.title }}
          </span>
          <v-row
            align="center"
            no-gutters
          >
            <v-col cols="auto">
              {{ $dayjs(eventDetails.data.start_at).format('dddd, MMMM D HH:mm') }} - {{ $dayjs(eventDetails.data.end_at).format('HH:mm') }}
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-chip
                label
                :color="eventTypes[eventDetails.data.event_type].color"
                dark
                small
              >
                {{ eventTypes[eventDetails.data.event_type].text }}
              </v-chip>
            </v-col>
          </v-row>
        </v-col>
        <v-spacer />
        <v-col cols="auto">
          <layout-selector
            :selected-layout.sync="selectedLayout"
            :layouts.sync="layouts"
            @add-layout="openAddLayoutDialog"
            @edit-layout="openEditLayoutDialog"
          />
        </v-col>
      </v-row>
      <v-divider class="mt-3" />
      <performance-individual
        v-if="eventDetails.data.id && player.data.id"
        :key="eventDetails.id + ' ' + player.data.id"
        class="mt-4"
        :player="player.data"
        :team="eventDetails.data.teams[0]"
        :event="eventDetails"
        :variables="variables.data"
        :layout="selectedLayout"
        :selected-exercise="selectedExercise"
      />
    </div>
  </div>
</template>

<script>
import { EventType, EventTypes } from '@/data/models'
import { DashboardType } from '@/data/models/Dashboard'
import PerformanceIndividual from '@/components/performance/PerformanceIndividual.vue'
import { VariableApiService } from '@/data/services/VariableApiService'
import { EventApiService } from '@/data/services'
import { cloneDeep } from 'lodash'
import { EventBus } from '@/plugins/bus'
import LayoutSelector from '@/components/LayoutSelector.vue'
import { RouteNames } from '@/router/routes'

export default {
  name: 'PerformanceEventPlayerView',
  components: {
    LayoutSelector,
    PerformanceIndividual
  },
  data() {
    return {
      eventDetails: {
        loading: false,
        data: {},
        maxValues: {}
      },
      player: {
        loading: false,
        data: {}
      },
      variables: {
        data: [],
        loading: false
      },
      customizeModal: false,
      dashboard: DashboardType.PLAYER_PERFORMANCE,
      layoutReady: false,
      selectedLayoutObject: null,
      selectedVariablesObject: null,
      exerciseId: null
    }
  },
  computed: {
    RouteNames() {
      return RouteNames
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    currentTeam() {
      return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    layouts() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.PLAYER_PERFORMANCE)
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return this.selectedLayoutObject
        }
        return cloneDeep(this.layouts[0])
      },
      set(value) {
        if (value !== this.selectedLayoutObject) this.layoutReady = false
        this.selectedLayoutObject = value
      }
    },
    selectedExercise() {
      return this.eventDetails?.data?.training_exercises?.find(exercise => exercise.id === this.exerciseId)
    }
  },
  watch: {
  },
  created() {
    this.eventTypeMatch = EventType.MATCH
    this.eventTypeTraining = EventType.TRAINING

    this.eventTypes = EventTypes

    if (this.$route.query.exerciseId) {
      this.exerciseId = this.$route.query.exerciseId

      this.$router.replace({ query: {} })
    }

    this.fetchEventDetails()
    this.fetchEventPlayerDetails()
    this.fetchVariables()
  },
  methods: {
    async fetchEventPlayerDetails() {
      try {
        this.player.loading = true

        const response = await EventApiService.getEventPlayerDetails(this.$route.params.eventId, this.$route.params.playerId)

        this.player.data = response.data
      } catch (e) {
        this.player.data = []
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_player.error'),
          color: 'error'
        })
      } finally {
        this.player.loading = false
      }
    },
    async fetchEventDetails() {
      try {
        this.eventDetails.loading = true

        const response = await EventApiService.getEventDetails(this.$route.params.eventId)

        this.eventDetails.data = response.data
      } catch (e) {
        this.eventDetails.data = []
        throw e
      } finally {
        this.eventDetails.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables()

        this.variables.data = result.data.map(v => {
          return {
            ...v,
            text: v.suffix ? this.$t(`variables.${v.value}`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}`),
            long_text: v.suffix ? this.$t(`variables.${v.value}.long`) + ' (' + this.$t(`suffixes.${v.suffix}`) + ')' : this.$t(`variables.${v.value}.long`)
          }
        })
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    getVariableName(value) {
      return this.variables.data.find(variable => variable.value === value)?.text
    },
    getVariableSuffix(value) {
      return this.variables.data.find(variable => variable.value === value)?.suffix
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: this.dashboard
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: this.dashboard,
        selectedLayout: this.selectedLayout
      })
    },
    goBack() {
      this.$router.push({ name: this.$routeNames.PERFORMANCE_EVENT, query: { event_id: this.eventDetails.data.id } })
    }
  }
}
</script>

<style scoped>

</style>
